import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Filter, FilterX } from "lucide-react"

import Filters from "components/Shared/Filters"
import MarkAttendanceModal from "./components/MarkAttendanceModal"
import {
  getAllTrialOperationsAttendanceList,
  getAllTrialOperationsEnrollmentList,
  getAllTrialOperationsInspectionList,
  getAllTrialOperationsReasonList,
  getAllTrialPeriods,
  getCateringList,
  updateSettings
} from "store/actions/trialPeriodAction"
import {
  setPayload,
  resetPayload,
  setActiveTab,
  toggleFilter,
  setCateringPayload,
  resetCateringPayload,
  setAttendancePayload,
  setEnrollmentPayload,
  resetAttendancePayload,
  resetEnrollmentPayload,
  setInspectionPayload,
  resetInspectionPayload
} from "store/reducers/trialPeriodReducer"
import { API_URL, formatDateForAPI } from "components/Common/Utils"
import DataTable from "./components/DataTable"
import CateringTable from "./components/CateringTable"
import { getJobTitlesList, getStationShiftDropdownList } from "store/actions/commonAction"
import Spinners from "../../components/Shared/Spinners"
import AttendanceTable from "./components/AttendanceTable"
import EnrolledTable from "./components/EnrolledTable"
import { usePermissions } from "hooks"
import axios from "axios"
import { showAlert } from "../../store/reducers/alertReducer"
import { getUserLists } from "../../store/actions/userAction"
import AbsentJobModal from "./components/AbsentJobModal"
import InspectionTable from "./components/InspectionTable"
import InspectionAttendanceModal from "./components/InspectionAttendanceModal"
import moment from "moment/moment"

const navItems = [
  { name: "Trial Operations", id: "trial_operation" },
  { name: "Not Enrolled", id: 0 },
  { name: "Enrolled", id: 1 },
  { name: "Present", id: "present" },
  { name: "Absent", id: "absent" },
  { name: "Attendance", id: "attendance" },
  { name: "Random Check", id: "inspection" },
  { name: "Catering", id: "catering" },
]

const TrialPeriod = () => {
  const navigate = useNavigate()
  const { t,  i18n: { language }} = useTranslation()
  const dispatch = useDispatch()
  const { showAll, permissions } = usePermissions("trial_run")
  const [isExporting, setIsExporting] = useState(false)
  const today = moment().format("DD-MM-YYYY")
  document.title = t("Trial Operation | Yamama")

  const {
    trialPeriods,
    totalPages,
    totalCount,
    currentPage,
    perPage,
    payload,
    allowTrialOperationSchedule,
    activeTab,
    isShowFilter,
    cateringPayload,
    caterings,
    loading,
    attendancePayload,
    inspectionPayload,
    enrollmentPayload,
    trialOperationsEnrollments,
    trialOperationsAttendance,
    trialOperationsInspectionList,
  } = useSelector(state => state.TrialPeriods)
  const { jobTitlesList, work_shifts, stations } = useSelector(state => state.Common)
  const { usersList } = useSelector(state => state.Users)

  const [isOpen, setIsOpen] = useState(false)
  const [isOpenAbsentJobModal, setIsOpenAbsentJobModal] = useState(false)
  const [isOpenInspectionAttendanceModal, setIsOpenInspectionAttendanceModal] = useState(false)
  const [trigger, setTrigger] = useState(false)
  const [openDropDown, setOpenDropDown] = useState(false)

  useEffect(() => {
    if (activeTab === "trial_operation") {
      getTrialOperations()
    } else if (activeTab === "catering") {
      getCateringData()
    } else if (activeTab === "attendance" || activeTab === "present" || activeTab === "absent") {
      getTrialOperationsAttendance()
    } else if (activeTab === "inspection") {
      getTrialOperationsInspections()
    } else if (activeTab === 1 || activeTab === 0) {
      getTrialOperationsEnrollments()
    }
  }, [trigger, activeTab])

  useEffect(() => {
    dispatch(getStationShiftDropdownList())
    dispatch(getAllTrialOperationsReasonList())
    dispatch(getJobTitlesList())
    dispatch(getUserLists())
  }, [])

  const getTrialOperations = () => {
    const data = {
      ...payload,
      filters: {
        ...payload.filters,
        date_range:
          payload?.filters?.date_range?.length === 2
            ? {
                start_date: formatDateForAPI(payload?.filters?.date_range[0]),
                end_date: formatDateForAPI(payload?.filters?.date_range[1]),
              }
            : {},
      },
    }

    dispatch(getAllTrialPeriods(data))
  }

  const getCateringData = () => {
    const data = {
      ...cateringPayload,

      filters: {
        ...cateringPayload.filters,
        date_range:
          cateringPayload?.filters?.date_range?.length === 2
            ? {
                start_date: formatDateForAPI(
                  cateringPayload?.filters?.date_range[0]
                ),
                end_date: formatDateForAPI(
                  cateringPayload?.filters?.date_range[1]
                ),
              }
            : {},
      },
    }

    dispatch(getCateringList(data))
  }

  const getTrialOperationsAttendance = () => {
    const data = {
      ...attendancePayload,

      filters: {
        ...attendancePayload.filters,
        type: activeTab === "attendance" ? 0 : activeTab === "present" ? 1 : activeTab === "absent" ? 2 : 0,
        station: parseInt(attendancePayload?.filters?.station),
        attendance_type: attendancePayload?.filters?.attendance_type || 0,
        date_range:
          attendancePayload?.filters?.date_range?.length === 2
            ? {
                start_date: formatDateForAPI(
                  attendancePayload?.filters?.date_range[0]
                ),
                end_date: formatDateForAPI(
                  attendancePayload?.filters?.date_range[1]
                ),
              }
            : {},
      },
    }

    dispatch(getAllTrialOperationsAttendanceList(data))
  }

  const getTrialOperationsInspections = () => {
    const data = {
      ...inspectionPayload,

      filters: {
        ...inspectionPayload.filters,
        station: parseInt(inspectionPayload?.filters?.station),
        type: inspectionPayload?.filters?.type || 0,
        date_range:
          inspectionPayload?.filters?.date_range?.length === 2
            ? {
              start_date: formatDateForAPI(
                inspectionPayload?.filters?.date_range[0]
              ),
              end_date: formatDateForAPI(
                inspectionPayload?.filters?.date_range[1]
              ),
            }
            : {},
      },
    }

    dispatch(getAllTrialOperationsInspectionList(data))
  }

  const getTrialOperationsEnrollments = () => {
    const data = {
      ...enrollmentPayload,

      filters: {
        ...enrollmentPayload.filters,
        type: activeTab,
        date_range:
          enrollmentPayload?.filters?.date_range?.length === 2
            ? {
                start_date: formatDateForAPI(
                  enrollmentPayload?.filters?.date_range[0]
                ),
                end_date: formatDateForAPI(
                  enrollmentPayload?.filters?.date_range[1]
                ),
              }
            : {},
      },
    }

    dispatch(getAllTrialOperationsEnrollmentList(data))
  }

  const toggleTab = tab => {
    if (activeTab !== tab) {
      dispatch(setActiveTab(tab))
      dispatch(resetPayload())
      dispatch(resetCateringPayload())
      dispatch(resetAttendancePayload())
      dispatch(resetEnrollmentPayload())
    }
  }

  const onPageChange = page => {
    if (activeTab === "trial_operation") {
      dispatch(setPayload({ page_number: page }))
    } else if (activeTab === "catering") {
      dispatch(setCateringPayload({ page_number: page }))
    } else if (activeTab === "attendance" || activeTab === "present" || activeTab === "absent") {
      dispatch(setAttendancePayload({ page_number: page }))
    } else if (activeTab === "inspection") {
      dispatch(setInspectionPayload({ page_number: page }))
    } else {
      dispatch(setEnrollmentPayload({ page_number: page }))
    }
    setTrigger(!trigger)
  }

  const handleReset = () => {
    if (activeTab === "trial_operation") {
      dispatch(resetPayload())
    } else if (activeTab === "catering") {
      dispatch(resetCateringPayload())
    } else if (activeTab === "attendance" || activeTab === "present" || activeTab === "absent") {
      dispatch(resetAttendancePayload())
    } else if (activeTab === "inspection") {
      dispatch(resetInspectionPayload())
    } else if (activeTab === 1 || activeTab === 0) {
      dispatch(resetEnrollmentPayload())
    }
    setTrigger(!trigger)
  }

  const handleOptionChange = (field, value) => {
    dispatch(updateSettings({ field, value }))
  }

  const getFilterState = () => {
    if (activeTab === "trial_operation") {
      return {
        ...payload.filters,
        order_by: payload.order_by,
      }
    } else if (activeTab === "catering") {
      return {
        ...cateringPayload.filters,
        order_by: cateringPayload.order_by,
      }
    } else if (activeTab === "attendance" || activeTab === "present" || activeTab === "absent") {
      return {
        ...attendancePayload.filters,
        order_by: attendancePayload.order_by,
      }
    } else if (activeTab === "inspection") {
      return {
        ...inspectionPayload.filters,
        order_by: inspectionPayload.order_by,
      }
    }
    else {
      return {
        ...enrollmentPayload.filters,
        order_by: enrollmentPayload.order_by,
      }
    }
  }

  const handleFilterDataChange = data => {
    const { order_by, ...rest } = data
    if (activeTab === "trial_operation") {
      dispatch(
        setPayload({
          order_by: Number(order_by),
          filters: { ...payload.filters, ...rest },
        })
      )
    } else if (activeTab === "catering") {
      dispatch(
        setCateringPayload({
          order_by: Number(order_by),
          filters: { ...cateringPayload.filters, ...rest },
        })
      )
    } else if (activeTab === "attendance" || activeTab === "present" || activeTab === "absent") {
      dispatch(
        setAttendancePayload({
          order_by: Number(order_by),
          filters: { ...attendancePayload.filters, ...rest },
        })
      )
    } else if (activeTab === "inspection") {
      dispatch(
        setInspectionPayload({
          order_by: Number(order_by),
          filters: { ...inspectionPayload.filters, ...rest },
        })
      )
    } else if (activeTab === 1 || activeTab === 0) {
      dispatch(
        setEnrollmentPayload({
          order_by: Number(order_by),
          filters: { ...enrollmentPayload.filters, ...rest },
        })
      )
    }
  }

  const stationsOptions = Object.entries(stations).map(([key, value]) => ({
    value: key,
    label: value[language === "en" ? "name" : "name_ar"],
  }))

  const shiftsOptions = Object.entries(work_shifts).map(([key, value]) => ({
    value: key,
    label: value[language === "en" ? "name" : "name_ar"],
  }))

  const formattedJobTitles = useMemo(() => {
    if (!jobTitlesList) return []

    return jobTitlesList.map(jobTitle => ({
      value: jobTitle.id,
      label: jobTitle[language === "en" ? "name" : "name_ar"],
    }))
  }, [jobTitlesList])

  const facilitators = useMemo(() => {
    if (!usersList) return []

    const roles = ["attendance admin"]
    const filteredUsers = (activeTab === "attendance" || activeTab === "present" || activeTab === "absent")
      ? usersList.filter(item => roles.some(role => item.role.toLowerCase().includes(role.toLowerCase())))
      : usersList

    return filteredUsers.map(user => {
      return {
        label: user.full_name,
        value: user.id,
      }
    })
  }, [usersList])

  const filters = {
    keyword: {
      type: "text",
      placeholder:
        activeTab === "attendance"
        || activeTab === "present"
        || activeTab === "absent"
        || activeTab === 0
        || activeTab === 1
        || activeTab === "inspection"
        ? t("Search by name,national id or phone number")
        : t("Search by name"),
      label: t("Search By"),
      colSize: 4,
    },
    station: {
      type: "select",
      placeholder: t("Select Station"),
      label: t("Station"),
      colSize: 4,
      options: stationsOptions,
    },
    shift: {
      type: "select",
      placeholder: t("Select Shift"),
      label: t("Shift"),
      colSize: 4,
      options: shiftsOptions,
    },
    attendance_admin_id: {
      type: "select",
      placeholder: activeTab === 'inspection'
        ? t("Select Random Check By")
        : t("Select Attendance Admin"),
      label: activeTab === 'inspection'
        ? t("Random Check By")
        : t("Attendance Admin"),
      colSize: 4,
      options: facilitators,
    },
    job_title_ids: {
      type: "select",
      placeholder: t("Select Job Title"),
      label: t("Job Title"),
      colSize: 4,
      options: formattedJobTitles,
      multiple: true,
    },
    attendance_type: {
      type: "select",
      placeholder: t("Select Attendance Type"),
      label: t("Attendance Type"),
      options: [
        { value: 0, label: t("All") },
        { value: 1, label: t("Early Check In") },
        { value: 2, label: t("Late Check In") },
        { value: 3, label: t("Early Check Out") },
        { value: 4, label: t("Late Check Out") },
      ],
    },
    type: {
      type: "select",
      placeholder: t("Select Random Check Status"),
      label: t("Random Check Status"),
      options: [
        { value: 0, label: t("All") },
        { value: 1, label: t("Present") },
        { value: 2, label: t("Absent") },
      ],
    },
    date_range: { type: "dateRange",
      label: t("Date Range")
    },
    order_by: {
      type: "select",
      placeholder: t("Select Order"),
      label: t("Select Order"),
      options: [
        { value: 1, label: t("Ascending") },
        { value: -1, label: t("Descending") },
      ],
    },
  }

  if (activeTab === 0 || activeTab === 1 ) {
    delete filters.shift;
    delete filters.station;
    delete filters.attendance_admin_id;
    delete filters.date_range;
    delete filters.attendance_type;
    delete filters.type;
  }
  if (activeTab === "trial_operation" || activeTab === "catering" ) {
    delete filters.shift;
    delete filters.station;
    delete filters.attendance_admin_id;
    delete filters.job_title_ids;
    delete filters.date_range;
    delete filters.attendance_type;
    delete filters.type;
  }
  if (activeTab === "attendance" || activeTab === "present" || activeTab === "absent") {
    delete filters.type;
  }
  if (activeTab === "inspection") {
    delete filters.attendance_type;
  }

  const handleExport = async () => {
    try {
      setIsExporting(true)
      const authUserString = localStorage.getItem("authUser")
      const authUser = authUserString ? JSON.parse(authUserString) : null
      const token = authUser?.token
      let api= ''

      let data = {}

      if (activeTab === "trial_operation") {
        api='/api/v1/admin/trial-run-operations/schedule/export'
        data = {
          trial_run_schedule_uuid: "",
          filters: {
          ...payload?.filters?.date_range,
            keyword: payload?.filters?.keyword || "",
          },

        }
      } else if (activeTab === "attendance" || activeTab === "present" || activeTab === "absent") {
        api='/api/v1/admin/trial-run-operations/schedule/attendances/export'
        data = {
          filters: {
            ...attendancePayload.filters,
            attendance_type: attendancePayload?.filters?.attendance_type || 0,
            date_range:
              attendancePayload?.filters?.date_range?.length === 2
                ? { start_date: formatDateForAPI(attendancePayload?.filters?.date_range[0]),
                  end_date: formatDateForAPI(attendancePayload?.filters?.date_range[1]),
                }
                : {},
            type: activeTab === "attendance" ? 0 : activeTab === "present" ? 1 : activeTab === "absent" ? 2 : 0,
            trial_run_schedule_uuid: null,
          },
        }
      } else if (activeTab === "inspection") {
        api='/api/v1/admin/trial-run-operations/schedule/random-check/export'
        data = {
          filters: {
            ...inspectionPayload.filters,
            trial_run_schedule_uuid: null,
          }
        }
      } else if (activeTab === 0 || activeTab === 1) {
        api='/api/v1/admin/trial-run-operations/schedule/invite-users/export'
        data = {
          filters: {
            ...enrollmentPayload.filters,
            keyword: enrollmentPayload?.filters?.keyword || "",
            trial_run_schedule_uuid: "",
            type: activeTab,
          }
        }
      } else {
        api='/api/v1/admin/catering/export'
        data ={
          filters: {
            ...cateringPayload?.filters,
            date_range: cateringPayload?.filters?.date_range,
            keyword: cateringPayload.filters.keyword || "",
            trial_run_schedule_uuid: "",
            is_trial_run: true
          }
        }
      }

      // `${API_URL}/api/v1/admin/export/by-type`,
      const response = await axios.post(
        `${API_URL}${api}`,
        data,
        {
          headers: {
            Authorization: token, // Attach token in headers
          },
          responseType: "blob", // Important for file downloads
        }
      )

      const contentType = response.headers["content-type"]

      if (contentType === "application/json") {
        return dispatch(
          showAlert({
            text: t("Export initiated, you will be notified once done."),
            type: "success",
          })
        )
      }

      // Create a URL for the downloaded file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        activeTab === "trial_operation"
          ? `Trial Operations ${today}.xlsx`
          : activeTab === "attendance"
          ? `Trial Operations Attended Candidates ${today}.xlsx`
          : activeTab === "present"
          ? `Trial Operations Present Candidates ${today}.xlsx`
          : activeTab === "absent"
          ? `Trial Operations Absent Candidates ${today}.xlsx`
          : activeTab === 0
          ? `Trial Operations Not Enrolled Candidates ${today}.xlsx`
          : activeTab === 1
          ? `Trial Operations Enrolled Candidates ${today}.xlsx`
          : activeTab === "inspection"
          ? `Trial Operations Random Checked Candidates ${today}.xlsx`
          : `Trial Operations Catering ${today}.xlsx`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Export failed:", error.response?.data || error.message)
    } finally {
      setIsExporting(false)
    }
  }

  if (loading) return <Spinners />

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs="12">
              <Row className="align-items-center">
                <Col xs="12" md="6" className="mb-2 mb-md-0">
                  <h4 className="card-title">{t("Trial Operation")}</h4>
                </Col>
                <Col xs="12" md="6">
                  <div className="d-flex flex-wrap justify-content-md-end gap-2">
                    {activeTab === "trial_operation" && (
                      <>
                        {(showAll || permissions.includes("can_create")) && (
                          <button
                            className="main_btn fit_content"
                            onClick={() => navigate("/trial-operation/create")}
                          >
                            {t("Add New")}
                          </button>
                        )}
                        {(showAll ||
                          permissions.includes("can_mark_attendance")) && (
                          <button
                            className="main_btn fit_content"
                            onClick={() => setIsOpen(true)}
                          >
                            {t("Capture Attendance")}
                          </button>
                        )}
                        {(showAll ||
                          permissions.includes("can_mark_attendance")) && (
                          <button
                            className="main_btn fit_content"
                            onClick={() => setIsOpenAbsentJobModal(true)}
                          >
                            {t("Capture Absent")}
                          </button>
                        )}
                        {(showAll ||
                          permissions.includes("can_capture_random_check")) && (
                          <button
                            className="main_btn fit_content"
                            onClick={() => setIsOpenInspectionAttendanceModal(true)}
                          >
                            {t("Capture Random Check")}
                          </button>
                        )}
                        <Dropdown
                          isOpen={openDropDown}
                          toggle={() => setOpenDropDown(!openDropDown)}
                        >
                          <DropdownToggle caret color="primary">
                            <button className="main_btn ">
                              {t("Options")}
                            </button>
                          </DropdownToggle>
                          <DropdownMenu className="mt-2">
                            <DropdownItem
                              className="d-flex align-items-center gap-2"
                              header
                            >
                              <input
                                type="checkbox"
                                checked={allowTrialOperationSchedule}
                                onClick={() =>
                                  handleOptionChange(
                                    "trial_operation_allow_schedule",
                                    !allowTrialOperationSchedule
                                  )
                                }
                                className="cursor-pointer"
                              />
                              <div>{t("Allow trial operation schedule")}</div>
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </>
                    )}
                    {(showAll || permissions.includes("enable_schedule_export") || permissions.includes("enable_schedule_export"))  && (
                      <button
                        className="main_btn fit_content"
                        onClick={handleExport}
                        disabled={isExporting}
                      >
                        {t("Download XLSX")}
                      </button>
                    )}
                    <Button
                      color="primary"
                      onClick={() => dispatch(toggleFilter())}
                      title={t("Show/Hide Filter")}
                    >
                      {isShowFilter ? <FilterX /> : <Filter />}
                    </Button>
                  </div>
                </Col>
              </Row>
              {isOpen && (
                <MarkAttendanceModal
                  isOpen={isOpen}
                  toggle={() => {
                    setIsOpen(false)
                    setTrigger(!trigger)
                  }}
                />
              )}
              {isOpenInspectionAttendanceModal && (
                <InspectionAttendanceModal
                  isOpen={isOpenInspectionAttendanceModal}
                  toggle={() => {
                    setIsOpenInspectionAttendanceModal(false)
                    setTrigger(!trigger)
                  }}
                />
              )}
              {isOpenAbsentJobModal && (
                <AbsentJobModal
                  isOpen={isOpenAbsentJobModal}
                  toggle={() => {
                    setIsOpenAbsentJobModal(false)
                    setTrigger(!trigger)
                  }}
                />
              )}

              <Nav tabs className="one_line_parent mb-3 mt-3">
                {navItems.map(item => (
                  <NavItem key={item.id}>
                    <NavLink
                      className={activeTab === item.id ? "active" : ""}
                      onClick={() => toggleTab(item.id)}
                    >
                      {t(item.name)}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              {isShowFilter && (
                <Filters
                  filters={filters}
                  state={getFilterState()}
                  setState={handleFilterDataChange}
                  handleSearch={() => setTrigger(!trigger)}
                  handleReset={handleReset}
                />
              )}

              <Row>
                <Col xs="12">
                  {activeTab === "trial_operation" && (
                    <DataTable
                      showAll={showAll}
                      permissions={permissions}
                      currentPage={currentPage}
                      totalCount={totalCount}
                      totalPages={totalPages}
                      data={trialPeriods}
                      onPageChange={onPageChange}
                      loading={loading}
                      trigger={() => setTrigger(!trigger)}
                      perPage={perPage}
                    />
                  )}

                  {activeTab === "catering" && (
                    <CateringTable
                      onPageChange={onPageChange}
                      data={caterings}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                      trigger={() => setTrigger(!trigger)}
                      perPage={perPage}
                    />
                  )}
                  {(activeTab === "attendance" || activeTab === "present" || activeTab === "absent") && (
                    <AttendanceTable
                      onPageChange={onPageChange}
                      data={trialOperationsAttendance}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                      trigger={() => setTrigger(!trigger)}
                      perPage={perPage}
                    />
                  )}
                  {(activeTab === 1 || activeTab === 0) && (
                    <EnrolledTable
                      onPageChange={onPageChange}
                      data={trialOperationsEnrollments}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                      trigger={() => setTrigger(!trigger)}
                      loading={loading}
                      perPage={perPage}
                    />
                  )}
                  {(activeTab === 'inspection') && (
                    <InspectionTable
                      onPageChange={onPageChange}
                      data={trialOperationsInspectionList}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                      trigger={() => setTrigger(!trigger)}
                      perPage={perPage}
                    />
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  )
}

export default TrialPeriod
